import {useState, useEffect, useMemo} from 'react'
import {safeSessionStorage} from '@/lib/storage'
import {useQuery, useMutation, useQueryClient} from '@tanstack/react-query'
import {useEntityMutation} from '@/hooks/useEntityMutation'
import {useDialog} from '@/hooks/useDialog'
import {DndContext, DragEndEvent, PointerSensor, useSensor, useSensors, closestCenter} from '@dnd-kit/core'
import {SortableContext, verticalListSortingStrategy, arrayMove} from '@dnd-kit/sortable'
import {FileText, FolderOpen, Globe, Plus, User} from 'lucide-react'
import {useNavigate} from 'react-router-dom'
import {routes} from '@/lib/routes'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {SmallText} from '@/components/shared/SmallText'
import {Strong} from '@/components/shared/Strong'
import {useAuth} from '@/contexts/auth-context'
import {permissions} from '@/lib/permissions'
import {useServerPagination} from '@/hooks/use-server-pagination'
import {ReportCategoryControllerCategoriesEndpoint} from '@/types/endpoint-contracts'
import {Button} from '@/components/ui/button'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {Textarea} from '@/components/ui/textarea'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {CONCEPT_ICON} from '@/lib/concepts'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {Badge} from '@/components/ui/badge'
import {Pagination} from '@/components/ui/pagination'
import {toast} from '@/hooks/use-toast'
import {formatDate} from '@/lib/date-utils'

import type {Page} from '@/types/page'
import {CategoryType, type ReportCategoryListItem, type CategoryTypeValue, type ReportDefinitionListItem, type ReportSearchResult} from './types'
import {CategoryRow} from './CategoryRow'
import {CategoryPickerDialog} from './CategoryPickerDialog'
import {SaveCancelFooter} from '@/components/shared/SaveCancelFooter'
import {DialogForm} from '@/components/shared/DialogForm'
import {FormGrid} from '@/components/shared/FormGrid'
import {InlineRow} from '@/components/shared/InlineRow'
import {Icon} from '@/components/shared/Icon'
import {MutedText} from '@/components/shared/MutedText'
import {PageLayout} from '@/components/shared/PageLayout'
import {SegmentedFilterBar} from '@/components/shared/SegmentedFilterBar'
import {UnifiedSearchBar} from '@/components/shared/UnifiedSearchBar'
import {IconText} from '@/components/shared/IconText'
import {Stack} from '@/components/shared/Stack'
import {HorizontalRule} from '@/components/shared/HorizontalRule'
import {ClickableTableRow} from '@/components/shared/ClickableTableRow'

function ReportSearchResultRow({
    result,
    onNavigate,
    i18n,
}: {
    result: ReportSearchResult
    onNavigate: (to: string) => void
    i18n: ReturnType<typeof useI18n>
}) {
    return (
        <ClickableTableRow
            data-testid={`report-search-result-${result.reportDefinitionId}`}
            onClick={() => onNavigate(routes.reporting.reports.detail(result.reportDefinitionId))}
        >
            <TableCell>
                <InlineRow gap="sm">
                    <Icon icon={FileText} size="sm" muted className="shrink-0"/>
                    <Stack gap="xs">
                        <Strong>{result.name}</Strong>
                        {result.description && (
                            <MutedText size="xs" block truncate className="max-w-xs">{result.description}</MutedText>
                        )}
                    </Stack>
                </InlineRow>
            </TableCell>
            <TableCell>
                <InlineRow gap="sm">
                    <Icon icon={FolderOpen} size="sm" muted className="shrink-0"/>
                    <SmallText>{result.categoryName}</SmallText>
                </InlineRow>
            </TableCell>
            <TableCell>
                <Badge variant="outline" className="min-w-[80px] justify-center">
                    <IconText
                        icon={result.categoryType === CategoryType.GLOBAL ? Globe : User}
                        size="xs"
                    >
                        {result.categoryType === CategoryType.GLOBAL
                            ? i18n.reporting.typeGlobal
                            : i18n.reporting.typePersonal}
                    </IconText>
                </Badge>
            </TableCell>
            <TableCell>
                <MutedText size="sm">{formatDate(result.updatedAt)}</MutedText>
            </TableCell>
        </ClickableTableRow>
    )
}

export function ReportCategoriesPage() {
    const services = useServices()
    const i18n = useI18n()
    const queryClient = useQueryClient()
    const {hasPermission} = useAuth()
    const canManageGlobal = hasPermission(permissions.reportingCategories.manage)

    // Dialogs
    const createDialog = useDialog()
    const [renameTarget, setRenameTarget] = useState<ReportCategoryListItem | null>(null)
    const [transferTarget, setTransferTarget] = useState<ReportCategoryListItem | null>(null)
    const [deleteTarget, setDeleteTarget] = useState<ReportCategoryListItem | null>(null)
    const [moveTarget, setMoveTarget] = useState<ReportCategoryListItem | null>(null)
    const [moveReportTarget, setMoveReportTarget] = useState<ReportDefinitionListItem | null>(null)
    const [createForm, setCreateForm] = useState({name: '', description: '', type: CategoryType.PERSONAL as CategoryTypeValue, parentCategoryId: ''})
    const [renameForm, setRenameForm] = useState({name: '', description: ''})
    // Picker dialogs for create-parent, move-category, and move-report
    const createParentPicker = useDialog()
    const [createParentName, setCreateParentName] = useState('')
    const moveCategoryPicker = useDialog()

    // Search mode: 'all' (default), 'categories', or 'reports' (cross-category report search)
    const [searchMode, setSearchMode] = useState<'all' | 'categories' | 'reports'>('all')
    const navigate = useNavigate()

    // Multi-level expansion for tree view — persisted in sessionStorage across navigation
    /* eslint-disable i18next/no-literal-string -- storage key */
    const storageKey = 'reporting-expanded-ids'
    /* eslint-enable i18next/no-literal-string */
    const [expandedIds, setExpandedIds] = useState<Set<string>>(() => {
        const stored = safeSessionStorage.getJSON<string[]>(storageKey)
        return stored ? new Set(stored) : new Set()
    })
    useEffect(() => {
        safeSessionStorage.setJSON(storageKey, [...expandedIds])
    }, [expandedIds])

    const {
        page, pageSize, queryString,
        setPage, setPageSize, pageSizeOptions,
        searchAndFilters, setSearchAndFilters,
    } = useServerPagination({
        endpoint: ReportCategoryControllerCategoriesEndpoint,
        defaultSortBy: ReportCategoryControllerCategoriesEndpoint.sort.DISPLAY_ORDER,
    })

    const search = searchAndFilters.q
    const typeFilter = (searchAndFilters.filters.TYPE as string | undefined) ?? null

    // WHY: Tree view (rootOnly) is the default — when the user types a q-string
    // OR selects a type filter, we collapse to a flat list across all levels.
    // The bar already writes ROOT_ONLY when the panel checkbox is set; here we
    // layer the implicit default for the no-filter case so the operator still
    // sees the tree root by default.
    const categoriesQueryParams = useMemo(() => {
        const params = Object.fromEntries(new URLSearchParams(queryString))
        const rootOnlyExplicit = params['rootOnly']
        const wantsFlat = !!search || !!typeFilter
        if (rootOnlyExplicit === undefined && !wantsFlat) {
            // eslint-disable-next-line i18next/no-literal-string -- boolean query param literal
            params['rootOnly'] = 'true'
        }
        return params
    }, [queryString, search, typeFilter])

    const {data: categoriesPage, isLoading, error} = useQuery<Page<ReportCategoryListItem>>({
        queryKey: ['reporting-categories', search, page, pageSize, typeFilter],
        queryFn: () => services.reporting.categories(categoriesQueryParams) as Promise<Page<ReportCategoryListItem>>,
    })

    // Cross-category report search
    const isReportSearch = searchMode === 'reports' && Boolean(search)
    const isAllSearch = searchMode === 'all' && Boolean(search)
    const {data: reportSearchPage, isLoading: reportSearchLoading} = useQuery<Page<ReportSearchResult>>({
        queryKey: ['reporting-report-search', search, page, pageSize],
        queryFn: () => services.reporting.searchReports({q: search, page, size: pageSize}),
        enabled: isReportSearch || isAllSearch,
    })

    const categories = categoriesPage?.content ?? []
    const totalItems = isReportSearch ? (reportSearchPage?.totalElements ?? 0) : (categoriesPage?.totalElements ?? 0)
    const totalPages = isReportSearch ? (reportSearchPage?.totalPages ?? 0) : (categoriesPage?.totalPages ?? 0)

    const dndSensors = useSensors(useSensor(PointerSensor, {activationConstraint: {distance: 5}}))

    /* eslint-disable i18next/no-literal-string -- DnD handler uses query keys and type identifiers */
    function handleDragEnd(event: DragEndEvent) {
        const {active, over} = event
        if (!over || active.id === over.id) return

        const activeType = active.data.current?.type as string | undefined
        const overType = over.data.current?.type as string | undefined

        if (activeType === 'category' && overType === 'category') {
            const activeParentId = active.data.current?.parentId as string | null
            const overParentId = over.data.current?.parentId as string | null

            if (activeParentId === overParentId) {
                // Same parent — reorder
                if (activeParentId === null) {
                    // Top-level categories
                    const oldIndex = categories.findIndex(c => c.categoryId === active.id)
                    const newIndex = categories.findIndex(c => c.categoryId === over.id)
                    if (oldIndex === -1 || newIndex === -1) return
                    const reordered = arrayMove(categories, oldIndex, newIndex)
                    const items = reordered.map((c, i) => ({id: c.categoryId, displayOrder: i}))
                    queryClient.setQueryData(
                        ['reporting-categories', search, page, pageSize, typeFilter],
                        (old: typeof categoriesPage) => old ? {...old, content: reordered} : old,
                    )
                    services.reporting.reorderCategories({items}).catch(() => {
                        queryClient.invalidateQueries({queryKey: ['reporting-categories']})
                    })
                } else {
                    // Nested children — use cached children data
                    const cached = queryClient.getQueryData<Page<ReportCategoryListItem>>(['reporting-category-children', activeParentId])
                    const childList = cached?.content ?? []
                    const oldIndex = childList.findIndex(c => c.categoryId === active.id)
                    const newIndex = childList.findIndex(c => c.categoryId === over.id)
                    if (oldIndex === -1 || newIndex === -1) return
                    const reordered = arrayMove(childList, oldIndex, newIndex)
                    const items = reordered.map((c, i) => ({id: c.categoryId, displayOrder: i}))
                    queryClient.setQueryData<Page<ReportCategoryListItem>>(
                        ['reporting-category-children', activeParentId],
                        old => old ? {...old, content: reordered} : old,
                    )
                    services.reporting.reorderCategories({items}).catch(() => {
                        queryClient.invalidateQueries({queryKey: ['reporting-category-children', activeParentId]})
                    })
                }
            } else {
                // Different parent — move category into the target category
                const version = active.data.current?.version as number | undefined
                if (version === undefined) return
                moveCategoryMutation.mutate({
                    id: active.id as string,
                    data: {parentCategoryId: over.id as string, version},
                })
            }
        } else if (activeType === 'report' && overType === 'report') {
            const sourceCategoryId = active.data.current?.categoryId as string
            const targetCategoryId = over.data.current?.categoryId as string

            if (sourceCategoryId === targetCategoryId) {
                // Same category — reorder reports
                const cached = queryClient.getQueryData<ReportDefinitionListItem[]>(['reporting-category-reports', sourceCategoryId])
                if (!cached) return
                const oldIndex = cached.findIndex(q => q.reportDefinitionId === active.id)
                const newIndex = cached.findIndex(q => q.reportDefinitionId === over.id)
                if (oldIndex === -1 || newIndex === -1) return
                const reordered = arrayMove(cached, oldIndex, newIndex)
                const items = reordered.map((q, i) => ({id: q.reportDefinitionId, displayOrder: i}))
                queryClient.setQueryData(['reporting-category-reports', sourceCategoryId], reordered)
                services.reporting.reorderReports({items}).catch(() => {
                    queryClient.invalidateQueries({queryKey: ['reporting-category-reports', sourceCategoryId]})
                })
            } else {
                // Different category — move report
                const version = active.data.current?.version as number | undefined
                if (version === undefined) return
                moveReportMutation.mutate({
                    id: active.id as string,
                    data: {targetCategoryId, version},
                })
            }
        } else if (activeType === 'report' && overType === 'category') {
            // Drop report on a category — move it there
            const targetCategoryId = over.id as string
            const sourceCategoryId = active.data.current?.categoryId as string
            if (sourceCategoryId === targetCategoryId) return
            const version = active.data.current?.version as number | undefined
            if (version === undefined) return
            moveReportMutation.mutate({
                id: active.id as string,
                data: {targetCategoryId, version},
            })
        }
    }
    /* eslint-enable i18next/no-literal-string */

    function invalidateCategoryQueries() {
        queryClient.invalidateQueries({queryKey: ['reporting-categories']})
        queryClient.invalidateQueries({queryKey: ['reporting-category-children']})
        queryClient.invalidateQueries({queryKey: ['category-picker']})
    }

    // Mutations
    const CATEGORY_QUERY_KEYS: Array<readonly string[]> = [
        ['reporting-categories'],
        ['reporting-category-children'],
        ['category-picker'],
    ]

    const createMutation = useMutation({
        mutationFn: (data: {name: string; description: string; type: string; parentCategoryId?: string | null}) =>
            services.reporting.createCategory(data),
        onSuccess: () => {
            invalidateCategoryQueries()
            createDialog.close()
            setCreateForm({name: '', description: '', type: CategoryType.PERSONAL, parentCategoryId: ''})
            setCreateParentName('')
            toast({title: i18n.reporting.categoryCreated as string})
        },
        onError: (error: Error) => {
            toast({variant: 'destructive', description: error.message})
        },
    })

    const renameMutation = useEntityMutation({
        mutationFn: ({id, data}: {id: string; data: {name: string; description: string; version: number}}) =>
            services.reporting.updateCategory(id, data),
        invalidate: CATEGORY_QUERY_KEYS,
        closeDialog: () => setRenameTarget(null),
        onSuccess: () => {
            toast({title: i18n.reporting.categoryUpdated as string})
        },
        onError: (error: Error) => {
            toast({variant: 'destructive', description: error.message})
        },
    })

    const transferMutation = useEntityMutation({
        mutationFn: ({id, data}: {id: string; data: {newType: CategoryTypeValue; version: number}}) =>
            services.reporting.transferCategory(id, data),
        invalidate: CATEGORY_QUERY_KEYS,
        closeDialog: () => setTransferTarget(null),
        onSuccess: () => {
            toast({title: i18n.reporting.categoryTransferred as string})
        },
        onError: (error: Error) => {
            toast({variant: 'destructive', description: error.message})
        },
    })

    const deleteCategoryMutation = useEntityMutation({
        mutationFn: (id: string) => services.reporting.removeCategory(id),
        invalidate: CATEGORY_QUERY_KEYS,
        closeDialog: () => setDeleteTarget(null),
        onSuccess: () => {
            toast({title: i18n.reporting.categoryDeleted as string})
        },
        onError: (error: Error) => {
            toast({variant: 'destructive', description: error.message})
        },
    })

    const moveCategoryMutation = useEntityMutation({
        mutationFn: ({id, data}: {id: string; data: {parentCategoryId: string | null; version: number}}) =>
            services.reporting.moveCategory(id, data),
        invalidate: CATEGORY_QUERY_KEYS,
        closeDialog: moveCategoryPicker.close,
        onSuccess: () => {
            setMoveTarget(null)
            toast({title: i18n.reporting.categoryMoved as string})
        },
        onError: (error: Error) => {
            moveCategoryPicker.close()
            setMoveTarget(null)
            toast({variant: 'destructive', description: error.message})
        },
    })

    function handleCreateSubmit(e: React.FormEvent) {
        e.preventDefault()
        if (!createForm.name.trim()) return
        createMutation.mutate({
            ...createForm,
            parentCategoryId: createForm.parentCategoryId || null,
        })
    }

    function handleRenameSubmit(e: React.FormEvent) {
        e.preventDefault()
        if (!renameTarget || !renameForm.name.trim()) return
        renameMutation.mutate({
            id: renameTarget.categoryId,
            data: {name: renameForm.name, description: renameForm.description, version: renameTarget.version},
        })
    }

    function openRenameDialog(collection: ReportCategoryListItem) {
        setRenameTarget(collection)
        setRenameForm({name: collection.name, description: collection.description ?? ''})
    }

    const moveReportMutation = useEntityMutation({
        mutationFn: ({id, data}: {id: string; data: {targetCategoryId: string; version: number}}) =>
            services.reporting.moveReport(id, data),
        invalidate: [...CATEGORY_QUERY_KEYS, ['reporting-category-reports']],
        closeDialog: () => setMoveReportTarget(null),
        onSuccess: () => {
            toast({title: i18n.reporting.reportMoved as string})
        },
        onError: (error: Error) => {
            toast({variant: 'destructive', description: error.message})
        },
    })

    function openMoveDialog(collection: ReportCategoryListItem) {
        setMoveTarget(collection)
        moveCategoryPicker.open()
    }

    function toggleExpanded(categoryId: string) {
        setExpandedIds(prev => {
            const next = new Set(prev)
            if (next.has(categoryId)) {
                next.delete(categoryId)
            } else {
                next.add(categoryId)
            }
            return next
        })
    }

    /* eslint-disable i18next/no-literal-string -- technical test-id identifiers and mode keys, not user-visible strings */
    const searchModeOptions = ([
        {value: 'all' as const, label: i18n.reporting.searchAll as string},
        {value: 'categories' as const, label: i18n.reporting.searchCategories as string},
        {value: 'reports' as const, label: i18n.reporting.searchReports as string},
    ]).map(option => ({
        ...option,
        testId: `btn-search-${option.value}`,
    }))
    const searchPlaceholder = searchMode === 'reports'
        ? (i18n.reporting.reportSearchPlaceholder as string)
        : searchMode === 'categories'
            ? (i18n.reporting.categorySearchPlaceholder as string)
            : (i18n.reporting.allSearchPlaceholder as string)
    /* eslint-enable i18next/no-literal-string */

    const filterControls = (
        <Stack gap="xs" className="w-full">
            <SegmentedFilterBar
                options={searchModeOptions}
                value={searchMode}
                onChange={setSearchMode}
                size="xs"
            />
            <UnifiedSearchBar
                contract={ReportCategoryControllerCategoriesEndpoint}
                value={searchAndFilters}
                onChange={setSearchAndFilters}
                placeholder={searchPlaceholder}
            />
        </Stack>
    )

    const actions = (
        <Button data-testid="btn-create-category" size="sm" onClick={createDialog.open}>
            <Icon icon={Plus} size="sm" className="mr-1"/>
            {i18n.reporting.createCategory}
        </Button>
    )

    return (
        <PageLayout
            variant="list"
            icon={CONCEPT_ICON.reportCategory}
            title={i18n.reporting.categories as string}
            count={totalItems}
            description={isReportSearch
                ? `${i18n.reporting.reportSearchResults({count: totalItems})} ${search ? i18n.common.matchingFilter : ''}`
                : search ? i18n.common.matchingFilter as string : undefined
            }
            filters={filterControls}
            actions={actions}
            loading={isLoading || (isReportSearch && reportSearchLoading) || (isAllSearch && reportSearchLoading)}
            error={error}
            data-testid="categories-page"
        >
                {isReportSearch ? (
                    /* Report search results — flat table */
                    <>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead data-testid="col-name">{i18n.common.name}</TableHead>
                                    <TableHead data-testid="col-category">{i18n.reporting.reportDetail.category}</TableHead>
                                    <TableHead data-testid="col-type">{i18n.common.type}</TableHead>
                                    <TableHead data-testid="col-last-modified">{i18n.reporting.lastModified}</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {(reportSearchPage?.content ?? []).map((result) => (
                                    <ReportSearchResultRow key={result.reportDefinitionId} result={result} onNavigate={navigate} i18n={i18n}/>
                                ))}
                                {(reportSearchPage?.content ?? []).length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={4} className="text-center py-8">
                                            <Stack gap="sm" align="center">
                                                <Icon icon={FileText} size="xl" muted/>
                                                <MutedText>{i18n.reporting.noReportsMatch}</MutedText>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                        {totalItems > 0 && (
                            <Pagination
                                page={page}
                                pageSize={pageSize}
                                totalItems={totalItems}
                                totalPages={totalPages}
                                onPageChange={setPage}
                                onPageSizeChange={setPageSize}
                                pageSizeOptions={pageSizeOptions}
                            />
                        )}
                    </>
                ) : (
                    /* Category tree view */
                    <>
                        <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead data-testid="col-name">{i18n.common.name}</TableHead>
                                        <TableHead data-testid="col-type">{i18n.common.type}</TableHead>
                                        <TableHead data-testid="col-actions" className="text-right w-[50px]">{i18n.common.actions}</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <SortableContext items={categories.map(c => c.categoryId)} strategy={verticalListSortingStrategy}>
                                    <TableBody>
                                        {categories.map((collection) => (
                                            <CategoryRow
                                                key={collection.categoryId}
                                                collection={collection}
                                                depth={0}
                                                expandedIds={expandedIds}
                                                onToggle={toggleExpanded}
                                                canManageGlobal={canManageGlobal}
                                                onRename={openRenameDialog}
                                                onTransfer={setTransferTarget}
                                                onMove={openMoveDialog}
                                                onDelete={setDeleteTarget}
                                                onMoveQuery={setMoveReportTarget}
                                                sortable={!search}
                                            />
                                        ))}
                                        {categories.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={3} className="text-center py-8">
                                                    <Stack gap="sm" align="center">
                                                        <Icon icon={FolderOpen} size="xl" muted/>
                                                        <MutedText>{search ? i18n.reporting.noCategoriesMatch : i18n.reporting.noCategories}</MutedText>
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </SortableContext>
                            </Table>
                        </DndContext>

                        {totalItems > 0 && !isAllSearch && (
                            <Pagination
                                page={page}
                                pageSize={pageSize}
                                totalItems={totalItems}
                                totalPages={totalPages}
                                onPageChange={setPage}
                                onPageSizeChange={setPageSize}
                                pageSizeOptions={pageSizeOptions}
                            />
                        )}

                        {/* In 'all' mode with search: also show report results below categories */}
                        {isAllSearch && (reportSearchPage?.content ?? []).length > 0 && (
                            <>
                                <HorizontalRule/>
                                <InlineRow gap="sm" className="mb-2">
                                    <Icon icon={FileText} size="sm" muted/>
                                    <Strong size="sm">
                                        {i18n.reporting.reportSearchResults({count: reportSearchPage?.totalElements ?? 0})}
                                    </Strong>
                                </InlineRow>
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead data-testid="col-name-search">{i18n.common.name}</TableHead>
                                            <TableHead data-testid="col-category-search">{i18n.reporting.reportDetail.category}</TableHead>
                                            <TableHead data-testid="col-type-search">{i18n.common.type}</TableHead>
                                            <TableHead data-testid="col-last-modified-search">{i18n.reporting.lastModified}</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {(reportSearchPage?.content ?? []).map((result) => (
                                            <ReportSearchResultRow key={result.reportDefinitionId} result={result} onNavigate={navigate} i18n={i18n}/>
                                        ))}
                                    </TableBody>
                                </Table>
                            </>
                        )}
                    </>
                )}
            {/* Create Category Dialog */}
            <Dialog open={createDialog.isOpen} onOpenChange={createDialog.bind.onOpenChange}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{i18n.reporting.createCategoryDialog.title}</DialogTitle>
                        <DialogDescription>{i18n.reporting.categoriesDesc}</DialogDescription>
                    </DialogHeader>
                    <DialogForm onSubmit={handleCreateSubmit}>
                        <FormGrid className="py-4">
                            <FormGrid>
                                <Label htmlFor="category-name">{i18n.reporting.createCategoryDialog.name}</Label>
                                <Input
                                    id="category-name"
                                    value={createForm.name}
                                    onChange={(e) => setCreateForm(prev => ({...prev, name: e.target.value}))}
                                    maxLength={255}
                                />
                            </FormGrid>
                            <FormGrid>
                                <Label htmlFor="category-desc">{i18n.reporting.createCategoryDialog.description}</Label>
                                <Textarea
                                    id="category-desc"
                                    value={createForm.description}
                                    onChange={(e) => setCreateForm(prev => ({...prev, description: e.target.value}))}
                                    maxLength={2000}
                                />
                            </FormGrid>
                            <FormGrid>
                                <Label htmlFor="category-type">{i18n.reporting.createCategoryDialog.type}</Label>
                                <Select
                                    value={createForm.type}
                                    onValueChange={(value) => {
                                        setCreateForm(prev => ({...prev, type: value as CategoryTypeValue, parentCategoryId: ''}))
                                        setCreateParentName('')
                                    }}
                                >
                                    <SelectTrigger data-testid="select-category-type">
                                        <SelectValue/>
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="PERSONAL">{i18n.reporting.createCategoryDialog.typePersonal}</SelectItem>
                                        {canManageGlobal && (
                                            <SelectItem value="GLOBAL">{i18n.reporting.createCategoryDialog.typeGlobal}</SelectItem>
                                        )}
                                    </SelectContent>
                                </Select>
                            </FormGrid>
                            <FormGrid>
                                <Label>{i18n.reporting.parentCategory}</Label>
                                <Button
                                    data-testid="btn-pick-parent-category"
                                    type="button"
                                    variant="outline"
                                    className="justify-start font-normal"
                                    onClick={createParentPicker.open}
                                >
                                    <Icon icon={FolderOpen} size="sm" muted className="mr-2"/>
                                    {createParentName || i18n.reporting.noParent}
                                </Button>
                            </FormGrid>
                        </FormGrid>
                        <SaveCancelFooter
                            testIdPrefix="create-category"
                            onCancel={createDialog.close}
                            saving={createMutation.isPending}
                            saveLabel={i18n.common.create}
                            savingLabel={i18n.common.creating}
                            disabled={!createForm.name.trim()}
                        />
                    </DialogForm>
                </DialogContent>
            </Dialog>

            {/* Rename Category Dialog */}
            <Dialog open={Boolean(renameTarget)} onOpenChange={() => setRenameTarget(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{i18n.reporting.renameDialog.title}</DialogTitle>
                        <DialogDescription>{i18n.reporting.renameDialog.description}</DialogDescription>
                    </DialogHeader>
                    <DialogForm onSubmit={handleRenameSubmit}>
                        <FormGrid className="py-4">
                            <FormGrid>
                                <Label htmlFor="rename-name">{i18n.common.name}</Label>
                                <Input
                                    id="rename-name"
                                    value={renameForm.name}
                                    onChange={(e) => setRenameForm(prev => ({...prev, name: e.target.value}))}
                                    maxLength={255}
                                />
                            </FormGrid>
                            <FormGrid>
                                <Label htmlFor="rename-desc">{i18n.common.description}</Label>
                                <Textarea
                                    id="rename-desc"
                                    value={renameForm.description}
                                    onChange={(e) => setRenameForm(prev => ({...prev, description: e.target.value}))}
                                    maxLength={2000}
                                />
                            </FormGrid>
                        </FormGrid>
                        <SaveCancelFooter
                            testIdPrefix="rename"
                            onCancel={() => setRenameTarget(null)}
                            saving={renameMutation.isPending}
                            saveLabel={i18n.common.saveChanges}
                            disabled={!renameForm.name.trim()}
                        />
                    </DialogForm>
                </DialogContent>
            </Dialog>

            {/* Transfer Category Dialog */}
            <Dialog open={Boolean(transferTarget)} onOpenChange={() => setTransferTarget(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{i18n.reporting.transferCategoryDialog.title}</DialogTitle>
                        <DialogDescription>{i18n.reporting.transferCategoryDialog.description}</DialogDescription>
                    </DialogHeader>
                        <SmallText className="py-4">
                            {transferTarget?.type === CategoryType.GLOBAL
                                ? i18n.reporting.transferCategoryDialog.toPersonal
                                : i18n.reporting.transferCategoryDialog.toGlobal}
                        </SmallText>
                    <SaveCancelFooter
                        testIdPrefix="transfer"
                        onCancel={() => setTransferTarget(null)}
                        saving={transferMutation.isPending}
                        saveLabel={i18n.reporting.transferCategory}
                        saveType="button"
                        onSave={() => {
                            if (!transferTarget) return
                            const newType = transferTarget.type === CategoryType.GLOBAL
                                ? CategoryType.PERSONAL
                                : CategoryType.GLOBAL
                            transferMutation.mutate({
                                id: transferTarget.categoryId,
                                data: {newType, version: transferTarget.version},
                            })
                        }}
                    />
                </DialogContent>
            </Dialog>

            {/* Delete Category Confirmation Dialog */}
            <Dialog open={Boolean(deleteTarget)} onOpenChange={() => setDeleteTarget(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{i18n.reporting.deleteCategory}</DialogTitle>
                        <DialogDescription>
                            {i18n.reporting.deleteCategoryConfirm({name: deleteTarget?.name ?? ''})}
                        </DialogDescription>
                    </DialogHeader>
                    <SaveCancelFooter
                        testIdPrefix="delete"
                        onCancel={() => setDeleteTarget(null)}
                        saving={deleteCategoryMutation.isPending}
                        saveLabel={i18n.common.delete}
                        savingLabel={i18n.common.deleting}
                        destructive
                        saveType="button"
                        onSave={() => { if (deleteTarget) deleteCategoryMutation.mutate(deleteTarget.categoryId) }}
                    />
                </DialogContent>
            </Dialog>

            {/* Move Category Picker */}
            <CategoryPickerDialog
                open={moveCategoryPicker.isOpen}
                onOpenChange={(open) => {
                    moveCategoryPicker.bind.onOpenChange(open)
                    if (!open) setMoveTarget(null)
                }}
                onSelect={(categoryId) => {
                    if (!moveTarget) return
                    moveCategoryMutation.mutate({
                        id: moveTarget.categoryId,
                        data: {parentCategoryId: categoryId, version: moveTarget.version},
                    })
                    moveCategoryPicker.close()
                    setMoveTarget(null)
                }}
                title={i18n.reporting.moveCategoryDialog.title as string}
                description={i18n.reporting.moveCategoryDialog.description as string}
                typeFilter={moveTarget?.type}
                excludeIds={moveTarget ? new Set([moveTarget.categoryId]) : undefined}
                showNoneOption
            />

            {/* Move Report Picker */}
            <CategoryPickerDialog
                open={Boolean(moveReportTarget)}
                onOpenChange={(open) => { if (!open) setMoveReportTarget(null) }}
                onSelect={(categoryId) => {
                    if (!moveReportTarget || !categoryId) return
                    moveReportMutation.mutate({
                        id: moveReportTarget.reportDefinitionId,
                        data: {targetCategoryId: categoryId, version: moveReportTarget.version},
                    })
                }}
                title={i18n.reporting.moveReportDialog.title as string}
                description={i18n.reporting.moveReportDialog.description as string}
                highlightIds={moveReportTarget ? new Set([moveReportTarget.categoryId]) : undefined}
            />

            {/* Create Category Parent Picker */}
            <CategoryPickerDialog
                open={createParentPicker.isOpen}
                onOpenChange={createParentPicker.bind.onOpenChange}
                onSelect={(categoryId, categoryName) => {
                    setCreateForm(prev => ({...prev, parentCategoryId: categoryId ?? ''}))
                    setCreateParentName(categoryId ? categoryName : '')
                }}
                title={i18n.reporting.parentCategory as string}
                typeFilter={createForm.type}
                showNoneOption
            />
        </PageLayout>
    )
}
